import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/ecom/v1/delivery-profiles',
        destPath: '/v1/delivery-profiles',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/delivery-profiles-service',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/delivery-profiles',
        destPath: '/v1/delivery-profiles',
      },
      {
        srcPath: '/ecom/v1/delivery-profiles/update-carrier-settings',
        destPath: '/v1/delivery-carriers/update-settings',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/ecom/v1/delivery-profiles',
        destPath: '/v1/delivery-profiles',
      },
      {
        srcPath: '/ecom/v1/delivery-profiles/update-carrier-settings',
        destPath: '/v1/delivery-carriers/update-settings',
      },
    ],
    _: [
      {
        srcPath: '/ecom/v1/delivery-profiles',
        destPath: '/v1/delivery-profiles',
      },
      {
        srcPath: '/ecom/v1/delivery-profiles/update-carrier-settings',
        destPath: '/v1/delivery-carriers/update-settings',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_delivery-profile';

/**
 * Creates a delivery profile.
 *
 *
 * A site can have up to 99 delivery profiles.
 * Attempting to exceed this limit returns a `DELIVERY_PROFILES_LIMIT_EXCEEDED` error.
 */
export function createDeliveryProfile(
  payload: object
): RequestOptionsFactory<any> {
  function __createDeliveryProfile({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'deliveryProfile.createdDate' },
          { path: 'deliveryProfile.updatedDate' },
          { path: 'deliveryProfile.deliveryRegions.createdDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.CreateDeliveryProfile',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createDeliveryProfile;
}

/** Retrieves a delivery profile. */
export function getDeliveryProfile(
  payload: object
): RequestOptionsFactory<any> {
  function __getDeliveryProfile({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.GetDeliveryProfile',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/{deliveryProfileId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDeliveryProfile;
}

/**
 * Updates a delivery profile.
 *
 * Each time the delivery profile is updated, `revision` increments by 1. The current `revision` must be passed when updating the delivery profile. This ensures you're working with the latest delivery profile and prevents unintended overwrites.
 *
 * This method doesn't support updating delivery regions in the profile. To manage delivery regions, use [Add Delivery Region](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/add-delivery-region), [Update Delivery Region](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/update-delivery-region), or [Remove Delivery Region](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/remove-delivery-region).
 */
export function updateDeliveryProfile(
  payload: object
): RequestOptionsFactory<any> {
  function __updateDeliveryProfile({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'deliveryProfile.createdDate' },
          { path: 'deliveryProfile.updatedDate' },
          { path: 'deliveryProfile.deliveryRegions.createdDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'PATCH' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.UpdateDeliveryProfile',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/{deliveryProfile.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateDeliveryProfile;
}

/**
 * Deletes a delivery profile.
 *
 *
 * Permanently deletes the delivery profile and its delivery regions and carrier configurations. Delivery regions are unique to each profile and aren't shared across profiles. This action can't be undone.
 *
 * The default delivery profile can't be deleted because every site requires one for core delivery functionality.
 */
export function deleteDeliveryProfile(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteDeliveryProfile({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'DELETE' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.DeleteDeliveryProfile',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/{deliveryProfileId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteDeliveryProfile;
}

/**
 * Retrieves a list of up to 100 delivery profiles, given the provided paging, filtering, and sorting.
 *
 * Query Delivery Profiles runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order.
 * - `paging.limit` is `50`.
 * - `paging.offset` is `0`.
 *
 * To learn how to query delivery profiles, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
 */
export function queryDeliveryProfiles(
  payload: object
): RequestOptionsFactory<any> {
  function __queryDeliveryProfiles({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.QueryDeliveryProfiles',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfiles.createdDate' },
              { path: 'deliveryProfiles.updatedDate' },
              { path: 'deliveryProfiles.deliveryRegions.createdDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
            protoPath: '/v1/delivery-profiles/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryDeliveryProfiles;
}

/** Adds a delivery region to an existing delivery profile. */
export function addDeliveryRegion(payload: object): RequestOptionsFactory<any> {
  function __addDeliveryRegion({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'deliveryRegion.createdDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.AddDeliveryRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/{deliveryProfileId}/delivery-region',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addDeliveryRegion;
}

/**
 * Updates a delivery region.
 *
 *
 * This method doesn't support updating delivery carriers in the region. To manage carriers, use [Add Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/add-delivery-carrier), [Update Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/update-delivery-carrier), or [Remove Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/remove-delivery-carrier).
 */
export function updateDeliveryRegion(
  payload: object
): RequestOptionsFactory<any> {
  function __updateDeliveryRegion({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'deliveryRegion.createdDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'PATCH' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.UpdateDeliveryRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath:
          '/v1/delivery-profiles/{deliveryProfileId}/delivery-region/{deliveryRegion.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateDeliveryRegion;
}

/** Retrieves a delivery profile by delivery region ID. */
export function getDeliveryProfileByDeliveryRegionId(
  payload: object
): RequestOptionsFactory<any> {
  function __getDeliveryProfileByDeliveryRegionId({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.GetDeliveryProfileByDeliveryRegionId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/delivery-regions/{deliveryRegionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDeliveryProfileByDeliveryRegionId;
}

/**
 * Removes a delivery region from a delivery profile.
 *
 *
 * You can remove all delivery regions from a profile, leaving the profile empty. An empty profile won't provide shipping options until a new delivery region is added.
 */
export function removeDeliveryRegion(
  payload: object
): RequestOptionsFactory<any> {
  function __removeDeliveryRegion({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'DELETE' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.RemoveDeliveryRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath:
          '/v1/delivery-profiles/{deliveryProfileId}/delivery-region/{deliveryRegionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removeDeliveryRegion;
}

/** Adds a delivery carrier to a delivery region. */
export function addDeliveryCarrier(
  payload: object
): RequestOptionsFactory<any> {
  function __addDeliveryCarrier({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.AddDeliveryCarrier',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/add-delivery-carrier',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addDeliveryCarrier;
}

/** Removes a delivery carrier from a delivery region. */
export function removeDeliveryCarrier(
  payload: object
): RequestOptionsFactory<any> {
  function __removeDeliveryCarrier({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.RemoveDeliveryCarrier',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/remove-delivery-carrier',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removeDeliveryCarrier;
}

/**
 * Updates a delivery carrier's settings in a delivery region.
 *
 *
 * This method updates an existing carrier's settings (such as backup rate and additional charges) using a partial update. Only the fields you provide are updated; other carrier settings remain unchanged.
 *
 * To add a new carrier to a region, use [Add Delivery Carrier](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/add-delivery-carrier).
 */
export function updateDeliveryCarrier(
  payload: object
): RequestOptionsFactory<any> {
  function __updateDeliveryCarrier({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'PATCH' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.UpdateDeliveryCarrier',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/update-delivery-carrier',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateDeliveryCarrier;
}

/**
 * Retrieves a list of all delivery carriers installed on a site.
 *
 *
 * Use this method to discover available carriers before adding them to delivery regions.
 */
export function listInstalledDeliveryCarriers(
  payload: object
): RequestOptionsFactory<any> {
  function __listInstalledDeliveryCarriers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.ListInstalledDeliveryCarriers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/installed-carriers',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listInstalledDeliveryCarriers;
}

/**
 * Retrieves delivery destination properties.
 *
 *
 * Returns properties for Rest of World, as well as aggregated country and subdivision data. Properties include whether postal codes are required and whether pickup is the only delivery option for each destination.
 */
export function getDeliveryDestinationProperties(
  payload: object
): RequestOptionsFactory<any> {
  function __getDeliveryDestinationProperties({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.GetDeliveryDestinationProperties',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/delivery-destination-properties',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getDeliveryDestinationProperties;
}

/**
 * Retrieves delivery carrier settings for a delivery profile.
 *
 *
 * Returns detailed carrier configurations, including regional settings for each carrier. This method provides more comprehensive information than [List Installed Delivery Carriers](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/shipping-delivery/delivery-profiles/list-installed-delivery-carriers).
 */
export function listDeliveryCarriers(
  payload: object
): RequestOptionsFactory<any> {
  function __listDeliveryCarriers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.ListDeliveryCarriers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath:
          '/v1/delivery-profiles/{deliveryProfileId}/delivery-carriers',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __listDeliveryCarriers;
}

/** Updates a carrier's external active settings. */
export function updateCarrierSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updateCarrierSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.UpdateCarrierSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-carriers/update-settings',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __updateCarrierSettings;
}

/** Sets a delivery carrier's active status. */
export function setDeliveryCarrierActiveStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __setDeliveryCarrierActiveStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.SetDeliveryCarrierActiveStatus',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/delivery-carriers/set-active-status',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __setDeliveryCarrierActiveStatus;
}

/** Updates extended fields of a delivery profile without incrementing revision. */
export function updateExtendedFields(
  payload: object
): RequestOptionsFactory<any> {
  function __updateExtendedFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.delivery_profile',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.deliveryprofile.v1.DeliveryProfiles.UpdateExtendedFields',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryprofileV1DeliveryProfilesUrl({
        protoPath: '/v1/delivery-profiles/{id}/update-extended-fields',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'deliveryProfile.createdDate' },
              { path: 'deliveryProfile.updatedDate' },
              { path: 'deliveryProfile.deliveryRegions.createdDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateExtendedFields;
}
